import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { textsApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';
import { 
  Search, 
  BookOpen, 
  Plus, 
  Trash2,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Language code to name mapping
const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian',
  pt: 'Portuguese', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
  ar: 'Arabic', fa: 'Persian', hi: 'Hindi', tr: 'Turkish', vi: 'Vietnamese',
  th: 'Thai', id: 'Indonesian', sw: 'Swahili', he: 'Hebrew', ps: 'Pashto',
  no: 'Norwegian', pl: 'Polish', uk: 'Ukrainian', nl: 'Dutch', sv: 'Swedish',
  da: 'Danish', fi: 'Finnish', el: 'Greek', cs: 'Czech', hu: 'Hungarian',
  ro: 'Romanian', hbo: 'Biblical Hebrew', grc: 'Biblical Greek',
};

// Compact date formatter — returns "Oct 19" style, null if no date
const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Helper to get language name
const getLanguageName = (text) => {
  // Prefer the server-resolved name (populated by get_texts for all text types)
  if (text.language_name) return text.language_name;
  // Legacy: language object attached directly
  if (text.language?.name) return text.language.name;
  // Legacy: source_language as a plain code string
  if (text.source_language && LANGUAGE_NAMES[text.source_language]) {
    return LANGUAGE_NAMES[text.source_language];
  }
  if (typeof text.language === 'string' && LANGUAGE_NAMES[text.language]) {
    return LANGUAGE_NAMES[text.language];
  }
  // Return the raw value if nothing matches
  return text.language?.name || text.source_language || text.language || null;
};

const ITEMS_PER_PAGE = 20;

export default function TextsList() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTexts = async () => {
    setLoading(true);
    try {
      const res = await textsApi.getAll({ 
        limit: ITEMS_PER_PAGE, 
        offset: page * ITEMS_PER_PAGE,
        search: search || undefined
      });
      setTexts(res.data);
      // Estimate total from response length
      if (res.data.length < ITEMS_PER_PAGE) {
        setTotalCount(page * ITEMS_PER_PAGE + res.data.length);
      } else {
        setTotalCount((page + 2) * ITEMS_PER_PAGE); // At least one more page
      }
    } catch (err) {
      toast.error(t('textsList.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTexts();
  }, [page, search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await textsApi.delete(deleteTarget.text_id);
      toast.success(t('textsList.deleteSuccess'));
      setDeleteTarget(null);
      fetchTexts();
    } catch (err) {
      toast.error(t('textsList.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="container max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('textsList.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('textsList.subtitle')}</p>
        </div>
        <Link to="/import">
          <Button className="gap-2" data-testid="import-new-text-btn">
            <Plus className="h-4 w-4" />
            Import New
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('textsList.search')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="pl-10"
          data-testid="search-texts-input"
        />
      </div>

      {/* Texts List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : texts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50" />
            <p className="mt-4 text-lg text-muted-foreground">
              {search ? 'No texts match your search' : 'No texts imported yet'}
            </p>
            {!search && (
              <Link to="/import">
                <Button className="mt-4 gap-2" data-testid="import-first-text-btn">
                  <Plus className="h-4 w-4" />
                  Import your first text
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {texts.map(text => {
            const progress = text.unique_words > 0 
              ? ((text.known_words || 0) / text.unique_words) * 100 
              : 0;
            
            return (
              <Card
                key={text.text_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`text-card-${text.text_id}`}
                onClick={() => navigate(`/texts/${text.text_id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold truncate ${/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text.title) ? 'font-arabic' : ''}`} dir="auto">{text.title}</h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                            <span>{text.word_count?.toLocaleString()} words</span>
                            <span>•</span>
                            <span>{text.unique_words?.toLocaleString()} unique</span>
                            {getLanguageName(text) && (
                              <>
                                <span>•</span>
                                <span>{getLanguageName(text)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center gap-3">
                        <Progress value={progress} className="flex-1 h-2" />
                        <span className="text-sm font-medium w-12 text-right">
                          {Math.round(progress)}%
                        </span>
                        {fmtDate(text.created_at) && (
                          <span className="text-xs text-muted-foreground opacity-50 flex-shrink-0">{fmtDate(text.created_at)}</span>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(text);
                      }}
                      data-testid={`delete-text-${text.text_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={texts.length < ITEMS_PER_PAGE}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('textsList.deleteDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This will also remove all word progress associated with this text. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('textsList.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
